/**
 * Button — themed text button with a 44px-minimum hit area.
 *
 * Two render modes:
 *
 *   1. **Sprite mode** — when the per-game theme has a `textureKey` for
 *      this variant, the button renders as a single Phaser Image stretched
 *      to the requested width/height, with a centered text label on top.
 *      Used by games that ship a GUI sprite pack (e.g. balloon-pop with
 *      Basic_GUI_Bundle).
 *
 *   2. **Graphics mode** (default) — flat-color rounded rect drawn with
 *      `Phaser.Graphics`, no asset dependencies. Used by every game that
 *      doesn't declare a theme.
 *
 * The mode is picked at construction time by reading
 * `scene.registry.get('theme').buttons[variant].textureKey` and checking
 * whether that texture exists in the scene's texture cache. If anything
 * is missing, sprite mode silently falls through to Graphics mode.
 */
import { Theme } from './Theme.js';

export class Button {
  /**
   * @param {object} opts
   * @param {Phaser.Scene} opts.scene
   * @param {number} opts.x
   * @param {number} opts.y
   * @param {string} opts.label
   * @param {() => void} opts.onClick
   * @param {number} [opts.width=220]
   * @param {number} [opts.height=Theme.minTouchPx]
   * @param {'primary'|'secondary'|'danger'} [opts.variant='primary']
   */
  constructor(opts) {
    const scene = opts.scene;
    const w = opts.width || 220;
    const h = Math.max(Theme.minTouchPx, opts.height || Theme.minTouchPx);
    const variant = opts.variant || 'primary';

    const container = scene.add.container(opts.x, opts.y);
    let bg;

    // Try sprite mode first.
    const textureKey = resolveTextureKey(scene, variant);
    if (textureKey) {
      bg = scene.add.image(0, 0, textureKey);
      bg.setDisplaySize(w, h);
      // Pixel-art textures should not get bilinearly blurred when scaled.
      if (bg.texture && bg.texture.setFilter) {
        try {
          bg.texture.setFilter(Phaser.Textures.FilterMode.NEAREST);
        } catch (_) { /* ignore */ }
      }
    } else {
      // Graphics fallback (existing flat look).
      const fillColor =
        variant === 'primary'  ? Theme.colors.primary :
        variant === 'danger'   ? Theme.colors.danger  :
                                 Theme.colors.surfaceAlt;
      bg = scene.add.graphics();
      bg.fillStyle(fillColor, 1);
      bg.fillRoundedRect(-w / 2, -h / 2, w, h, Theme.radius);
      bg.lineStyle(2, 0xffffff, 0.15);
      bg.strokeRoundedRect(-w / 2, -h / 2, w, h, Theme.radius);
    }

    const text = scene.add.text(0, 0, opts.label, {
      fontFamily: Theme.font.family,
      fontSize: Theme.font.sizes.md,
      color: Theme.colors.text,
      fontStyle: 'bold'
    }).setOrigin(0.5);

    container.add([bg, text]);
    container.setSize(w, h);
    container.setInteractive({ useHandCursor: true });

    container.on('pointerover', () => bg.setAlpha(0.85));
    container.on('pointerout',  () => bg.setAlpha(1));
    container.on('pointerdown', () => {
      container.setScale(0.96);
      // Unlock audio on the *down* edge — on iOS the gesture context
      // is alive during the touchstart/pointerdown handler, and the
      // unlock sound has to start inside that stack frame. Waiting
      // until pointerup sometimes misses the gesture window.
      tryUnlockAudio(scene);
    });
    container.on('pointerup', () => {
      container.setScale(1);
      if (opts.onClick) opts.onClick();
    });

    this.container = container;
    this.label = text;
  }

  setLabel(str) {
    this.label.setText(str);
  }

  destroy() {
    this.container.destroy();
  }
}

/**
 * Force-unlock the audio pipeline from inside a guaranteed user-gesture
 * stack frame. iOS Safari requires `AudioContext.resume()` / Phaser's
 * `sound.unlock()` to be called synchronously in a real gesture handler
 * — our window-level unlock listener doesn't get Phaser's canvas
 * pointer events (they don't always bubble), so buttons have to do it
 * themselves on every press.
 */
function tryUnlockAudio(scene) {
  try {
    const audio = scene && scene.registry && scene.registry.get('audio');
    if (audio && typeof audio._forceUnlock === 'function') {
      audio._forceUnlock();
    }
  } catch (_) { /* ignore */ }
}

/**
 * Look up the textureKey for a button variant from the per-game theme,
 * confirm the texture is loaded, and return the key (or null).
 */
function resolveTextureKey(scene, variant) {
  if (!scene.registry) return null;
  const theme = scene.registry.get('theme');
  if (!theme || !theme.buttons || !theme.buttons[variant]) return null;
  const key = theme.buttons[variant].textureKey;
  if (!key) return null;
  if (!scene.textures || !scene.textures.exists(key)) return null;
  return key;
}
